import {
  AMAZON_APP_STORE,
  APPLE_APP_STORE,
  GOOGLE_PLAY_STORE,
  PH_SUBSCRIPTION,
  RECURLY,
  RESELLER,
} from "../../constants/spCode";
import { IExternalPaymentData } from "../../types/common";

export const PROVIDERS = [
  RECURLY,
  APPLE_APP_STORE,
  GOOGLE_PLAY_STORE,
  AMAZON_APP_STORE,
  RESELLER,
  PH_SUBSCRIPTION,
];

export const RECURLY_INPUT_STYLE: any = {
  fontWeight: "300",
  transition: "background-color 50000s ease-in-out 0s",
};

export const DEFAULT_CURRENCY = "USD";

// The value type says the map is total, but it is not: it holds four sp_codes
// and is indexed by an arbitrary one. It survives today only because
// isNonRecurlyPayment happens to admit exactly the four keys below — add a fifth
// non-Recurly sp_code without adding it here and a bare deref blanks the payment
// page. Typing it `| undefined` would say so but breaks the consumers, so the
// `?.` at the lookups is deliberate and must not be tidied away as dead code.
export const EXTERNAL_SUBSCRIPTION_DETAILS: Record<
  string,
  IExternalPaymentData
> = {
  [GOOGLE_PLAY_STORE]: {
    company: "Google",
    supportLInk: "https://support.google.com/googleplay/answer/7018481",
    label: "Google Play",
  },
  [APPLE_APP_STORE]: {
    company: "Apple",
    supportLInk: "https://support.apple.com/en-us/HT202039",
    label: "Apple Store",
  },
  // Amazon Appstore (Fire OS) IAP — store-managed like Apple/Google. The link is
  // Amazon's "Manage Your Appstore Subscriptions from the Website" help article,
  // the web-facing equivalent of the Apple/Google ones above.
  [AMAZON_APP_STORE]: {
    company: "Amazon",
    supportLInk:
      "https://www.amazon.com/gp/help/customer/display.html?nodeId=GDT2SXJJ7K49JKYE",
    label: "Amazon Appstore",
  },
  // Managed by the PH subscription service; its payment method is external
  // (BTCPay Server crypto, no Recurly card), but unlike Apple/Google, plan
  // changes and cancellation stay in-portal — see isPhSubscriptionManaged /
  // PH_SUBSCRIPTION_MANAGED_SP_LIST in constants/spCode.ts. There is no
  // self-service support article to link to, so supportLInk is intentionally
  // empty; render sites skip the link when it is unset.
  //
  // Labelled "Subscription", not "Crypto": the sp_code covers everything the
  // subscription service bills, and crypto/BTCPay is only today's payment rail
  // (`company` still names it, for the rare place that shows the rail itself).
  [PH_SUBSCRIPTION]: {
    company: "BTCPay Server",
    supportLInk: "",
    label: "Subscription",
  },
};

export const validationRules = {
  number: {
    size: {
      uint: 4294967295,
    },
    pattern: {
      noLeadingZeroInt: /^(?:[1-9][0-9]*|0)$/,
      // eslint-disable-next-line prefer-named-capture-group
      onlyNumber: /^[0-9]\d*(\.\d+)?$/,
      wholeNumber: /^[0-9]+$/,
    },
  },
  capitalizeLetters_AndNumbers: /^[A-Z0-9][A-Z0-9_]+[A-Z0-9]$/,
};

export const SERVICE_CODE = {
  VPN: "VPN",
  USENET: "USENET",
};

export const FEATURE_CODE = {
  FEATURE_NATFIREWALL: "FEATURE_NATFIREWALL",
};

export const RESPONSE_ERROR_KEY = {
  canceledSubscriptionCanNotBeChanged: "CanceledSubscriptionCanNotBeChanged",
  spSubscriptionIsNotFound: "SpSubscriptionIsNotFound",
  PrincipalWithEmailAlreadyExists: "PrincipalWithEmailAlreadyExists",
  PrincipalWithUsernameAlreadyExists: "usernameAlreadyExists",
  Unauthorized: "Unauthorized",
  SessionHasExpired: "SessionHasExpired",
  Illegal: "ILLEGAL_CHARACTERS",
  DeviceTimeIncorrect: "DeviceTimeIncorrect",
  LoginAttemptsExceeded: "LoginAttemptsExceeded",
};

export const RESPONSE_ERROR_PROBLEM = {
  EmailValidator: "EmailValidator",
  AlreadyExists: "ALREADY_EXISTS",
  InvalidValue: "INVALID_VALUE",
  PasswordMismatch: "PASSWORD_MISMATCH",
  Illegal: "ILLEGAL_CHARACTERS",
  OwnerAssigned: "OWNER_ALREADY_ASSIGNED",
  OutOfRange: "OUT_OF_RANGE",
  UserHasActiveSubscription: "USER_HAS_ACTIVE_SUBSCRIPTION",
};

export const COUPON_TYPE = {
  couponCode: "COUPON_CODE",
};

export const PLAN_DESCRIPTIONS = {
  all: [
    "connectionsPerAccount",
    "noLogFullyOwned",
    "globalServerLocations",
    "accessToStreamingLibraries",
  ],
  treeYear: ["bestValueOfPlans"],
};

const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_MINUTE = 60;
const MINUTE_IN_HOUR = 60;
const HOURS_IN_DAY = 24;

export const MILLISECOND_IN_DAY =
  HOURS_IN_DAY * MINUTE_IN_HOUR * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;
export const MILLISECOND_IN_MINUTE = SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND;
export const BYTES_IN_GB = 1000000000;

export const PAYMENT_STATES = {
  ADD_PAYMENT_METHOD: "AddPaymentMethod",
  UPDATE_PAYMENT_METHOD: "UpdatePaymentMethod",
};

export const SUBSCRIPTION_STATUS = {
  pendingPayment: "pendingPayment",
  paymentFailure: "paymentFailure",
  active: "active",
  notActive: "notActive",
  pendingCancellation: "pendingCancellation",
  cancelled: "cancelled", // it is mean that subscription is not active and expiration date has come
  paused: "paused",
  subscriptionTerminated: "subscriptionTerminated",
  onHold: "onHold",
  expired: "expired",
};
